import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/lib/reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!emailRegex.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Tell us a bit about your goal";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success("Thanks — we'll get back within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Unable to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-white/5 bg-gradient-to-b from-[#060606] to-black"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF7B00]">
              — Let's talk
            </span>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Ready to grow?{" "}
              <span className="text-gradient-gold">Start the conversation.</span>
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-md">
              Tell us about your brand and your goals. We'll respond within one
              business day with a custom starting point.
            </p>
          </Reveal>

          <Reveal delay={260}>
            <div className="mt-10 space-y-4">
              <a
                data-testid="contact-phone"
                href="tel:+918853623126"
                className="group flex items-center gap-4 rounded-2xl border border-white/5 bg-[#0F0F0F] p-5 hover:border-[#FF7B00]/30 transition-colors"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF7B00]/10 border border-[#FF7B00]/20 text-[#FF7B00]">
                  <Phone className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                    Call
                  </div>
                  <div className="font-display text-lg font-bold text-white">
                    88536 23126
                  </div>
                </div>
              </a>

              <a
                data-testid="contact-email"
                href="mailto:sales@maturemspl.store"
                className="group flex items-center gap-4 rounded-2xl border border-white/5 bg-[#0F0F0F] p-5 hover:border-[#FF7B00]/30 transition-colors"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF7B00]/10 border border-[#FF7B00]/20 text-[#FF7B00]">
                  <Mail className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                    Email
                  </div>
                  <div className="font-display text-lg font-bold text-white">
                    sales@maturemspl.store
                  </div>
                </div>
              </a>

              <button
                type="button"
                data-testid="contact-whatsapp"
                onClick={() =>
                  toast("Drop your number in the form — we'll WhatsApp you.", {
                    description: "Our team reaches out within 24 hours.",
                  })
                }
                className="whatsapp-pulse group flex w-full items-center justify-between gap-4 rounded-2xl border border-[#FF7B00]/40 bg-gradient-to-r from-[#FF7B00]/10 to-transparent p-5 text-left hover:border-[#FF7B00] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF7B00] text-black">
                    <MessageCircle className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#FFB347]">
                      WhatsApp
                    </div>
                    <div className="font-display text-lg font-bold text-white">
                      Chat with us instantly
                    </div>
                  </div>
                </div>
                <span className="text-[#FF7B00] font-bold">→</span>
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={120}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/5 bg-[#0F0F0F] p-6 md:p-10"
              noValidate
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2"
                  >
                    Your Name
                  </label>
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Cooper"
                    className="w-full rounded-xl border border-white/10 bg-[#070707] px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#FF7B00] focus:ring-2 focus:ring-[#FF7B00]/30 transition"
                  />
                  {errors.name && (
                    <p
                      data-testid="contact-error-name"
                      className="mt-2 text-xs text-red-400"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@brand.com"
                    className="w-full rounded-xl border border-white/10 bg-[#070707] px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#FF7B00] focus:ring-2 focus:ring-[#FF7B00]/30 transition"
                  />
                  {errors.email && (
                    <p
                      data-testid="contact-error-email"
                      className="mt-2 text-xs text-red-400"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="message"
                  className="block text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your brand, goals, and the channels you want to scale..."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#070707] px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#FF7B00] focus:ring-2 focus:ring-[#FF7B00]/30 transition"
                />
                {errors.message && (
                  <p
                    data-testid="contact-error-message"
                    className="mt-2 text-xs text-red-400"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-5">
                <p className="text-xs text-zinc-500 max-w-xs">
                  By submitting, you agree to be contacted by our team
                  regarding your enquiry.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF7B00] to-[#FF9933] px-7 py-3.5 text-sm font-bold text-black shadow-[0_0_20px_rgba(255,123,0,0.3)] hover:shadow-[0_0_40px_rgba(255,123,0,0.55)] transition-all hover:scale-[1.03] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Message <Send className="h-4 w-4" strokeWidth={2.25} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
